import { Flex, Stack } from '@chakra-ui/react'

import FormContentBlock from './FormContentBlock'
import FormQuestionMetadata from './FormQuestionMetadata'
import type {
  AlignInputs,
  FormBlock,
  FormBlocksComponentProps,
  FormErrors,
  FormQuestion,
  RenderQuestion,
} from './types'

function FormInputItem({
  input,
  error,
  renderQuestion,
}: {
  input: FormQuestion
  error?: string
  renderQuestion: RenderQuestion
}): React.ReactElement {
  return (
    <>
      {input.label != null ? (
        <Flex direction="column" flex={4}>
          <FormQuestionMetadata input={input} error={error} />
        </Flex>
      ) : null}
      <Flex direction="column" flex={5}>
        {renderQuestion(input)}
        {input.description == null ? null : (
          <Flex direction="column" mt="16px">
            <FormContentBlock content={input.description} />
          </Flex>
        )}
      </Flex>
    </>
  )
}

function FormRowHorizontal({
  blocks,
  errors,
  renderQuestion,
  alignInputs,
}: {
  blocks: FormBlock[]
  errors: FormErrors
  renderQuestion: RenderQuestion
  alignInputs?: AlignInputs
}): React.ReactElement | null {
  return (
    <Stack direction="column" align="left">
      {blocks.map(block => (
        <Flex key={block.name} flex={block.sizeSpan ?? block.size} width="100%">
          <Stack
            direction={{ base: 'column', sm: 'row' }}
            flex={{ base: 'auto', md: block.size }}
            width={{ base: '100%', sm: 'auto' }}
            maxWidth="100%"
            align={alignInputs ?? undefined}
            spacing="12px"
          >
            {block.display === 'display' ? (
              <FormContentBlock content={block.content} />
            ) : (
              <FormInputItem
                input={block}
                error={errors[block.name]}
                renderQuestion={renderQuestion}
              />
            )}
          </Stack>
          {block.sizeSpan != null && block.sizeSpan > 0 ? (
            <Flex flex={block.sizeSpan - block.size} display={{ base: 'none', sm: 'flex' }} />
          ) : null}
        </Flex>
      ))}
    </Stack>
  )
}

export default function FormBlocksHorizontal({
  rows,
  errors,
  renderQuestion,
  alignInputs,
}: FormBlocksComponentProps): React.ReactElement {
  return (
    <Stack direction="column" spacing={{ base: '24px', sm: '40px' }}>
      {rows.map(row => (
        <FormRowHorizontal
          key={row.key}
          errors={errors}
          blocks={row.blocks}
          renderQuestion={renderQuestion}
          alignInputs={alignInputs}
        />
      ))}
    </Stack>
  )
}
